"use client";

import {
  createContext,
  useContext,
  useState,
  useMemo,
  type Dispatch,
  type ReactNode,
  type SetStateAction,
} from "react";
import { useQuery } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";
import { fetchCoursePricingPlans, type PricingPlan } from "../api";
import { backedPlanFeatures, type CourseClaim } from "../lib/course-claims";

export type PricingMode = "loading" | "legacy" | "single" | "multi";

type PricingContextValue = {
  /** Active pricing plans sorted by `sortOrder`. */
  plans: PricingPlan[];
  /**
   * Heuristic “recommended” tier for badges and hints only (popular badge → isDefault → first).
   * Does not imply the learner has chosen this plan.
   */
  recommendedPlan: PricingPlan | null;
  /**
   * The plan the page sells. In `multi` mode it is the learner's explicit pick, or, until
   * they pick, `recommendedPlan` (`SITE-CRS01`, founder decision 1, 2026-10-05: Enroll
   * shows a price in the first screen).
   */
  selectedPlan: PricingPlan | null;
  selectedPlanId: string | null;
  /**
   * Sets the explicit pick. An updater's `prev` is the explicit pick only, `null` while
   * the page still shows the recommended default, so the owned-tier sync
   * (`prev === null ? ownedId : prev`) still moves an owner onto their own tier.
   */
  setSelectedPlanId: Dispatch<SetStateAction<string | null>>;
  isLoading: boolean;
  isError: boolean;
  mode: PricingMode;
};

const CoursePricingContext = createContext<PricingContextValue | null>(null);

export function useCoursePricing(): PricingContextValue {
  const ctx = useContext(CoursePricingContext);
  if (!ctx) throw new Error("useCoursePricing must be inside CoursePricingProvider");
  return ctx;
}

export function CoursePricingProvider({
  slug,
  previewToken,
  initialPlans,
  unbackedClaims,
  children,
}: {
  slug: string;
  previewToken?: string | null;
  /** The plans the server read for this page (`fetchCoursePricingPlansServer`); absent in preview. */
  initialPlans?: PricingPlan[] | null;
  /** `GR-TRU01`: claims the course's record does not back; a plan feature making one is left out. */
  unbackedClaims?: CourseClaim[];
  children: ReactNode;
}) {
  const seeded = !previewToken && initialPlans != null;
  const {
    data: rawPlans = [],
    isLoading,
    isError,
  } = useQuery({
    queryKey: [...queryKeys.courses.pricingPlans(slug), previewToken ? "preview" : "public"],
    queryFn: () => fetchCoursePricingPlans(slug, previewToken),
    staleTime: 5 * 60 * 1000,
    retry: 1,
    ...(seeded ? { initialData: initialPlans } : {}),
  });

  const unbackedKey = (unbackedClaims ?? []).join(",");
  const plans = useMemo(() => {
    const unbacked = new Set(unbackedKey ? (unbackedKey.split(",") as CourseClaim[]) : []);
    return [...rawPlans]
      .sort((a, b) => a.sortOrder - b.sortOrder)
      .map((plan) => (unbacked.size > 0 ? { ...plan, features: backedPlanFeatures(plan.features, unbacked) } : plan));
  }, [rawPlans, unbackedKey]);

  const mode: PricingMode = isLoading
    ? "loading"
    : isError || plans.length === 0
      ? "legacy"
      : plans.length === 1
        ? "single"
        : "multi";

  const recommendedPlan = useMemo<PricingPlan | null>(() => {
    if (plans.length === 0) return null;
    const popular = plans.find((p) =>
      p.badge_en?.toLowerCase().includes("popular"),
    );
    const def = plans.find((p) => p.isDefault);
    return popular ?? def ?? plans[0] ?? null;
  }, [plans]);

  const [explicitPlanId, setSelectedPlanId] = useState<string | null>(null);

  const selectedPlan = useMemo<PricingPlan | null>(() => {
    if (mode === "loading" || mode === "legacy") return null;
    if (mode === "single") return plans[0] ?? null;
    if (mode === "multi") {
      const picked =
        explicitPlanId === null ? null : plans.find((p) => p._id === explicitPlanId);
      return picked ?? recommendedPlan;
    }
    return null;
  }, [mode, plans, explicitPlanId, recommendedPlan]);
  const selectedPlanId = selectedPlan?._id ?? null;

  return (
    <CoursePricingContext.Provider
      value={{
        plans,
        recommendedPlan,
        selectedPlan,
        selectedPlanId,
        setSelectedPlanId,
        isLoading,
        isError,
        mode,
      }}
    >
      {children}
    </CoursePricingContext.Provider>
  );
}
